import { saveAs } from "file-saver";
import produce from "immer";
import {
  requestDeleteFile,
  requestUploadFiles,
} from "src/requests/files/requestCreators";
import { FileDTO, FileHandleResponse } from "src/requests/files/types";
import { fileToDto } from "src/utils/files";
import flowioast from "src/utils/flowioast";
import {
  DOWNLOAD_FILE_NATIVE,
  invokeRNMessage,
  isNativeApp,
  OPEN_FILE_NATIVE,
} from "src/utils/nativeUtils";
import {
  AsyncVoidFncType,
  DeleteMultipleParams,
  DeleteParams,
  GetFileParams,
  RawFile,
  UploadMultipleParams,
} from "../types";
import { canBeFileOpenedInBrowser, getBlob } from "./fileHelper.utils";

const downloadFileWeb: AsyncVoidFncType<GetFileParams> = async (params) => {
  try {
    const blob = await getBlob(params);
    saveAs(blob, params.file.fileName);
  } catch (e) {
    flowioast.error.fromIntlId("segments.file_input.download.error", e);
  }
};

const openFileWeb: AsyncVoidFncType<GetFileParams> = async (params) => {
  // some files cant be opened in browser, so have to download them instead of open
  const formatFileName = (fileParams: GetFileParams) =>
    produce(fileParams, (draft) => {
      draft.file.fileName = params.file.fileName?.trim();
    });
  if (!canBeFileOpenedInBrowser(params.file.fileName)) {
    downloadFileWeb(formatFileName(params));
    return;
  }

  try {
    const blob = await getBlob(params);
    const _URL = window.URL || window.webkitURL; // eslint-disable-line no-underscore-dangle
    const fileUrl = _URL.createObjectURL(blob);
    window.open(fileUrl);
  } catch (e) {
    flowioast.error.fromIntlId("segments.file_input.download.error", e);
  }
};

const downloadFileNative: AsyncVoidFncType<GetFileParams> = async (params) => {
  invokeRNMessage(DOWNLOAD_FILE_NATIVE, {
    templateId: params.templateId,
    templateItemId: params.templateItemId,
    ...params.file,
  });
};

const openFileNative: AsyncVoidFncType<GetFileParams> = async (params) => {
  invokeRNMessage(OPEN_FILE_NATIVE, {
    templateId: params.templateId,
    templateItemId: params.templateItemId,
    ...params.file,
  });
};

export const openFile: AsyncVoidFncType<GetFileParams> = async (params) => {
  if (isNativeApp()) return openFileNative(params);
  return openFileWeb(params);
};

export const downloadFile: AsyncVoidFncType<GetFileParams> = async (params) => {
  if (isNativeApp()) return downloadFileNative(params);
  return downloadFileWeb(params);
};

export const getFilesForUpload = (files: RawFile[]): Promise<FileDTO[]> => {
  const fileDTOPromises = files.map(({ file, id }) => fileToDto(file, id));
  return Promise.all(fileDTOPromises);
};

export const uploadAllNewFiles = async ({
  files,
  ...rest
}: UploadMultipleParams): Promise<FileHandleResponse> => {
  const filesDto = await getFilesForUpload(files);
  const { data } = await requestUploadFiles({ filesDto, ...rest });
  return data;
};

const deleteFile: AsyncVoidFncType<DeleteParams> = async ({
  file,
  ...rest
}) => {
  await requestDeleteFile({
    ...rest,
    fileId: file.id,
  });
};

export const deleteAllRemovedFiles = ({
  files,
  ...rest
}: DeleteMultipleParams): Promise<void[]> => {
  const deleteFilesPromises = files.map((file) =>
    deleteFile({ file, ...rest } as DeleteParams)
  );
  return Promise.all(deleteFilesPromises);
};
